import type { CountdownFormats } from '@shared/types/options';

const MAX_VALUES = {
  D: 30, // max 30 days
  H: 23, // max 23 hours
  M: 59, // max 59 minutes
  S: 59 // max 59 seconds
};

const UNIT_SECONDS = {
  D: 86400, // days to seconds
  H: 3600, // hours to seconds
  M: 60, // minutes to seconds
  S: 1 // seconds
};

/**
 * Validate and normalize time parts based on unit maximums
 * @param parts - Array of time parts
 * @param format - Format string
 * @returns Normalized time parts
 */
export function normalizeTimeParts(parts: string[], format: CountdownFormats): string[] {
  // Convert parts to numbers
  const numParts = parts.map((part) => parseInt(part, 10));

  // Work from the smallest unit upwards
  for (let i = numParts.length - 1; i > 0; i--) {
    const unit = format[i];
    const maxValue = MAX_VALUES[unit as keyof typeof MAX_VALUES];

    // If current unit exceeds max, normalize
    if (numParts[i] > maxValue) {
      // Calculate overflow
      const overflow = Math.floor(numParts[i] / (unit === 'S' ? 60 : maxValue + 1));

      // Cap current unit to max value
      numParts[i] = Math.min(numParts[i], maxValue);

      // Add to next larger unit
      numParts[i - 1] += overflow;
    }
  }

  // Cap the largest unit to its max value
  const largestUnit = format[0];
  const largestMaxValue = MAX_VALUES[largestUnit as keyof typeof MAX_VALUES];
  numParts[0] = Math.min(numParts[0], largestMaxValue);

  return numParts.map((part) => part.toString().padStart(2, '0'));
}

/**
 * Convert a time string to total seconds based on the given format
 * @param timeStr - Time string to convert (e.g., '03:23:23:11')
 * @param format - Format string determining the order of time units
 *                 Supports: 'DHMS', 'DHM', 'HMS', 'HM', 'MS'
 * @returns Total number of seconds
 */
export function timeToSeconds(timeStr: string, format: CountdownFormats = 'DHMS'): number {
  // Remove any extra spaces and split the string
  const parts = timeStr.replace(/\s/g, '').split(':');

  // Validate that the number of parts matches the format
  if (parts.length !== format.length) return 0;

  // Normalize parts first
  const normalizedParts = normalizeTimeParts(parts, format);

  // Calculate total seconds
  return normalizedParts.reduce((total, value, index) => {
    return total + parseInt(value) * UNIT_SECONDS[format[index] as keyof typeof UNIT_SECONDS];
  }, 0);
}

/**
 * Convert total seconds to a time string based on the given format
 * @param totalSeconds - Total number of seconds to convert
 * @param format - Format string determining the order of time units
 *                 Supports: 'DHMS', 'DHM', 'HMS', 'HM', 'MS'
 * @returns Formatted time string
 */
export function secondsToTime(totalSeconds: number, format: CountdownFormats = 'DHMS'): string {
  // Calculate each time unit
  let remainingSeconds = totalSeconds;

  return format
    .split('')
    .map((unit) => {
      // Calculate the value for this unit
      const value = Math.floor(remainingSeconds / UNIT_SECONDS[unit as keyof typeof UNIT_SECONDS]);

      // Remove the calculated seconds
      remainingSeconds %= UNIT_SECONDS[unit as keyof typeof UNIT_SECONDS];

      // Zero-pad to two digits and ensure max value
      return Math.min(value, MAX_VALUES[unit as keyof typeof MAX_VALUES])
        .toString()
        .padStart(2, '0');
    })
    .join(':');
}

/**
 * Converts total seconds into an object with time units based on the given format
 * @param seconds Total time in seconds
 * @param format Format string (DHMS, DHM, HMS, HM, MS)
 * @param padded Whether to pad single digit values with leading zeros
 * @returns Object with time units according to format
 */
export function formatSecondsAsTimeObject(
  seconds: number,
  format: CountdownFormats,
  padded: boolean = false
): Record<string, string | number> {
  // Calculate all possible time units
  const days = Math.floor(seconds / (24 * 60 * 60));
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((seconds % (60 * 60)) / 60);
  const secs = Math.floor(seconds % 60);

  const result: Record<string, string | number> = {};

  // Function to handle padding if needed
  const formatValue = (value: number): string | number => {
    if (padded) return value.toString().padStart(2, '0');
    return value;
  };

  // Add appropriate units based on format
  if (format.includes("D")) {
    result["D"] = formatValue(days);
  } else {
    // If days aren't in the format, add their value to hours
    const totalHours = days * 24 + hours;
    if (format.includes("H")) {
      result["H"] = formatValue(totalHours);
    } else {
      // If hours aren't in the format either, add their value to minutes
      const totalMinutes = totalHours * 60 + minutes;
      if (format.includes("M")) {
        result["M"] = formatValue(totalMinutes);
      }
    }
  }

  // Add remaining units if they're in the format
  if (format.includes("H") && !("H" in result)) {
    result["H"] = formatValue(hours);
  }

  if (format.includes("M") && !("M" in result)) {
    result["M"] = formatValue(minutes);
  }

  if (format.includes("S")) {
    result["S"] = formatValue(secs);
  }

  return result;
}
